import { useCallback, useEffect, useEffectEvent, useRef } from "react";
import {
  generatePath,
  NavigationType,
  useLocation,
  useMatch,
  useNavigate,
  useNavigationType,
} from "react-router";
import { useShallow } from "zustand/react/shallow";

import useChatConversation from "shared/ChatConversation/useChatConversation";
import useChatConversationCreate from "shared/ChatConversation/useChatConversationCreate";
import useChatConversationDelete from "shared/ChatConversation/useChatConversationDelete";
import { AiIntegrationView } from "shared/AiIntegration/useSearchAiIntegrations/gql";

import { ASSISTANT_PAGE_PATH, ASSISTANT_PAGE_ROUTE } from "../../constants";
import useChatStore from "../store/useStore";
import { createChat, isChatStatusBusy } from "../helpers";

type UseAssistantPanelChatNavigationParams = {
  defaultIntegration: AiIntegrationView | undefined;
  integrationLoading: boolean;
  refetchConversations: () => void;
};

const useAssistantPanelChatNavigation = ({
  defaultIntegration,
  integrationLoading,
  refetchConversations,
}: UseAssistantPanelChatNavigationParams) => {
  const [createConversation] = useChatConversationCreate();
  const pageMatch = useMatch(ASSISTANT_PAGE_ROUTE);
  const isAssistantPageMode = !!pageMatch;
  const routeChatId = pageMatch?.params.chatId;
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const locationKey = useLocation().key;
  const lastSyncedLocationKeyRef = useRef<string | undefined>(undefined);
  const isMountedRef = useRef(false);

  const { loadConversation } = useChatConversation();
  const [deleteConversation] = useChatConversationDelete();

  const {
    startChat,
    openChat,
    resetChat,
    setConversationError,
    setOpeningChatId,
    chatId,
    openingChatId,
  } = useChatStore(
    useShallow((state) => ({
      startChat: state.startChat,
      openChat: state.openChat,
      resetChat: state.resetChat,
      setConversationError: state.setConversationError,
      setOpeningChatId: state.setOpeningChatId,
      chatId: state.chat?.id,
      openingChatId: state.openingChatId,
    }))
  );

  const handleFirstMessage = useCallback(
    async (message: string) => {
      setOpeningChatId(undefined);
      const result = await createConversation({});
      const state = useChatStore.getState();
      if (state.chat || state.openingChatId || state.conversationError) return;

      const id = result?.data?.intentChatConversationCreate.id;

      if (id) {
        startChat(createChat(id), message);
        refetchConversations();

        if (isAssistantPageMode && isMountedRef.current) {
          navigate(generatePath(ASSISTANT_PAGE_ROUTE, { chatId: id }));
        }
      } else {
        setConversationError("Failed to create conversation");
      }
    },
    [
      setOpeningChatId,
      createConversation,
      refetchConversations,
      startChat,
      setConversationError,
      isAssistantPageMode,
      navigate,
    ]
  );

  const handleNewChat = useCallback(() => {
    resetChat();

    if (isAssistantPageMode) {
      navigate(ASSISTANT_PAGE_PATH);
    }
  }, [resetChat, isAssistantPageMode, navigate]);

  const handleDeleteChat = useCallback(
    async (deletedChatId: string) => {
      const { openingChatId: loadingChatId, chat } = useChatStore.getState();

      if (deletedChatId === (loadingChatId ?? chat?.id) || deletedChatId === routeChatId) {
        resetChat();

        if (isAssistantPageMode) {
          navigate(ASSISTANT_PAGE_PATH, { replace: true });
        }
      }

      await deleteConversation({ id: deletedChatId });
      refetchConversations();
    },
    [
      deleteConversation,
      refetchConversations,
      resetChat,
      isAssistantPageMode,
      navigate,
      routeChatId,
    ]
  );

  const loadChat = useCallback(
    async (selectedChatId: string) => {
      resetChat();
      setOpeningChatId(selectedChatId);

      const result = await loadConversation(selectedChatId).catch(() => null);
      if (useChatStore.getState().openingChatId !== selectedChatId) return;

      if (result) {
        openChat({
          chat: createChat(selectedChatId, result.messages),
          version: result.version,
          integrationId: result.integrationId ?? defaultIntegration?.id,
          model: result.model ?? defaultIntegration?.modelDetails[0]?.id,
        });
      } else {
        setConversationError("Could not open this conversation");
        setOpeningChatId(undefined);
      }
    },
    [
      loadConversation,
      resetChat,
      setOpeningChatId,
      openChat,
      setConversationError,
      defaultIntegration?.id,
      defaultIntegration?.modelDetails,
    ]
  );

  const handleSelectChat = useCallback(
    (selectedChatId: string) => {
      if (selectedChatId === (openingChatId ?? chatId)) return;

      if (isAssistantPageMode) {
        navigate(generatePath(ASSISTANT_PAGE_ROUTE, { chatId: selectedChatId }));
      } else {
        loadChat(selectedChatId);
      }
    },
    [openingChatId, chatId, isAssistantPageMode, navigate, loadChat]
  );

  const syncChatWithRoute = useEffectEvent((chatIdFromRoute: string | undefined) => {
    const storeChatId = useChatStore.getState().chat?.id;
    const isHistoryMove =
      navigationType === NavigationType.Pop &&
      lastSyncedLocationKeyRef.current !== undefined &&
      lastSyncedLocationKeyRef.current !== locationKey;
    lastSyncedLocationKeyRef.current = locationKey;

    if (chatIdFromRoute && chatIdFromRoute !== storeChatId) {
      loadChat(chatIdFromRoute);
    } else if (!chatIdFromRoute && storeChatId && isHistoryMove) {
      resetChat();
    } else if (!chatIdFromRoute && storeChatId) {
      navigate(generatePath(ASSISTANT_PAGE_ROUTE, { chatId: storeChatId }), { replace: true });
    } else if (!chatIdFromRoute) {
      setOpeningChatId(undefined);
      setConversationError(undefined);
    }
  });

  const handleReloadChat = useCallback(async () => {
    const chat = useChatStore.getState().chat;
    if (!chat) return;

    const result = await loadConversation(chat.id).catch(() => null);
    const isStale = useChatStore.getState().chat !== chat || isChatStatusBusy(chat.status);
    if (!result || isStale) return;

    // Same instance on purpose: replacing it would drop the error the composer shows.
    chat.messages = result.messages;
    openChat({
      chat,
      version: result.version,
      integrationId: result.integrationId,
      model: result.model,
    });
  }, [loadConversation, openChat]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (isAssistantPageMode && !integrationLoading) {
      syncChatWithRoute(routeChatId);
    }
  }, [isAssistantPageMode, integrationLoading, routeChatId]);

  return {
    routeChatId,
    conversationLoading: openingChatId !== undefined,
    handleFirstMessage,
    handleNewChat,
    handleDeleteChat,
    handleSelectChat,
    handleReloadChat,
  };
};

export default useAssistantPanelChatNavigation;
